import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { connect } from 'react-redux';
import {
  fetchUser,
  updateUserSettings,
  fetchAccount
} from '#action/user/settings';
import UserForm from '#component/user/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import validate from '#component/user/form/validate';

const SettingsUserForm = props => {
  const {
    user,
    account,
    project,
    initialValues,
    updateUserSettings: dispatchUpdateUserSettings,
    fetchUser: dispatchFetchUser,
    fetchAccount: dispatchFetchAccount
  } = props;

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: initialValues
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const handleFormSubmit = async values => {
    const QUALITY_MANAGER_TYPE = 1;
    const {
      firstName,
      lastName,
      phoneNumber,
      email,
      userType,
      projectId,
      disabled,
      measurementSystem,
      defaultPage
    } = values;

    const userSettingsData = {
      firstName: firstName?.trim(),
      lastName: lastName?.trim(),
      // API validation will not allow empty sting or null
      phoneNumber: phoneNumber || undefined,
      email,
      userType: parseInt(userType, 10),
      allowManageAlertAcceptance: 0,
      defaultProjectId: projectId,
      disabled: disabled ? 0 : 1,
      measurementSystem,
      defaultPage
    };

    if (userType === `${QUALITY_MANAGER_TYPE}`) {
      userSettingsData.allowManageAlertAcceptance = QUALITY_MANAGER_TYPE;
      userSettingsData.userType = account.userType;
    }

    try {
      await dispatchUpdateUserSettings(
        user.id,
        userSettingsData
      );

      await dispatchFetchUser(user.id);

      if (account.id === user.id) {
        await dispatchFetchAccount({ ignoreLoading: true });
      }

      reset(values, { keepDirty: false });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off" style={{ width: '100%' }}>
        <UserForm
          title="User profile"
          {...props}
          isSubmitting={methods.formState.isSubmitting}
          isDirty={methods.formState.isDirty}
          project={project}
        />
      </form>
    </FormProvider>
  );
};

function mapStateToProps(state) {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    userType,
    disabled,
    measurementSystem,
    defaultPage,
    defaultProject,
    directProjects: projects = [],
    requestStatus,
    allowManageAlertAcceptance
  } = state.user;
  const { user, account, breadcrumbs } = state;
  let editable = false;
  const withoutProjects = !projects.length;
  // Fallback for selectedProject, no longer tracked in form reducer
  const selectedProject = defaultProject ? {
    ...defaultProject,
    label: `${defaultProject.title}`,
    value: defaultProject.id
  } : null;

  if (user.id === account.id) {
    editable = true;
  }

  const initialSelectedProject = defaultProject ? {
    ...defaultProject,
    label: `${defaultProject.title}`,
    value: defaultProject.id
  } : null;

  const uType = allowManageAlertAcceptance === 1 ? allowManageAlertAcceptance : userType;

  return {
    initialValues: {
      firstName: firstName || '',
      lastName: lastName || '',
      phoneNumber: phoneNumber || '',
      email: email || '',
      userType: `${uType}`,
      disabled: !disabled,
      measurementSystem: `${measurementSystem}`,
      project: initialSelectedProject,
      projectId: initialSelectedProject?.value,
      defaultPage: defaultPage || '/device'
    },
    user,
    requestStatus,
    account,
    editable,
    hidePassword: editable,
    projects,
    breadcrumbs,
    settingsUserForm: true,
    withoutProjects,
    selectedProject
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchUser: userId => {
      dispatch(fetchUser(userId));
    },
    fetchAccount: props => {
      dispatch(fetchAccount(props));
    },
    updateUserSettings: (userId, data) =>
      dispatch(updateUserSettings(userId, data)),
    dispatch
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SettingsUserForm);
